import {Plural, Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, ReactNode, useCallback} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {
  VisualBasicCollapsedActivity,
  VisualBasicCollapsedActivityTypesEnum,
} from '../../../../../../srcCommon/inboxes/viewer/activity/visualActivity/visualBasicCollapsedActivity';
import {buildTeammateDisplayName} from '../../../../../helpers/modelHelpers';
import {ActivityTypesEnum, BasicActivityTypesEnum} from '../../../../../models/api/activityModel';
import {
  InterfaceEventsEnum,
  TimelineActivityActionsEnum,
  trackInterfaceEvent,
} from '../../../../../services/tracking';
import {InteractiveDateTimeFormatterContainer} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatterContainer';

/*
 * Props.
 */

// Represents the details needed to render collapsed activities in a prioritized manner.
export interface CollapsedHeaderActivity {
  id: number;
  date: number;
  activityType: ActivityTypesEnum;
  iconName: IconName;
  text: ReactNode;
  priority: number;
}

interface BasicGroupHeaderProps {
  userId: number;
  collapsedHeaderActivities: ReadonlyArray<VisualBasicCollapsedActivity>;
  isExpanded: boolean;
  onExpandClick: Handler;
  onCollapseClick: Handler;
}

interface StyledGroupButtonProps {
  isExpanded: boolean;
}

/*
 * Style.
 */

const StyledTimestampWrapperDiv = styled.div`
  width: 50%;
  vertical-align: middle;

  text-align: right;
  justify-self: end;

  text-transform: uppercase;
  font-size: ${fontSizes.tiny};
  color: ${(p) => p.theme.greys.shade70};

  /* Visible on hover. */
  opacity: 0;
  &:hover {
    opacity: 1;
  }
`;
const StyledTimestampDiv = styled.div`
  position: relative;
  margin-top: 16px;
`;

const StyledWrapperDiv = styled.div`
  display: flex;
  justify-content: center;

  /* We do not want this cell's width to affect the centering of the rest of this collapse group. */
  max-width: 0;
  white-space: nowrap;

  &:hover + ${StyledTimestampWrapperDiv} {
    opacity: 1;
  }
`;

const StyledGroupButton = styled(ChromelessButton)<StyledGroupButtonProps>`
  position: relative;
  padding: 5px 12px;
  margin-top: 16px;

  border-radius: 100px;

  background-color: ${(p) => (p.isExpanded ? p.theme.alphas.gray10 : 'none')};

  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.verySmall};

  &:hover {
    color: ${(p) => p.theme.greys.shade90};
    background-color: ${(p) => (p.isExpanded ? p.theme.alphas.gray20 : p.theme.alphas.gray10)};
  }
`;

const StyledIconDiv = styled(Icon)`
  display: inline-block;
  margin-right: 6px;
  margin-bottom: -3px;
`;

const StyledTextDiv = styled.div`
  display: inline-block;
`;

const StyledSeeLessDiv = styled.div`
  padding: 0px 12px;
`;

// These span are needed to selectively capitalize the first word inside the <Trans />.
const StyledFirstWordSpan = styled.span<{$isFirst: boolean}>`
  text-transform: ${(p) => (p.$isFirst ? 'capitalize' : 'none')};
`;
const StyledActivitySpan = styled.span``;

/*
 * Component.
 */

/* Header for a group of activities which can display a collapsed state with prioritized details. */
export const BasicGroupHeader: FC<BasicGroupHeaderProps> = (props) => {
  const {isExpanded, onExpandClick, onCollapseClick} = props;

  const onClick = useCallback(() => {
    if (isExpanded) {
      onCollapseClick();
      return;
    }

    onExpandClick();

    // We want to track only when an activity is expanded.
    trackInterfaceEvent(InterfaceEventsEnum.TIMELINE_ACTIVITY_ACTION, {
      action: TimelineActivityActionsEnum.CLICK_GROUP_EXPAND,
    });
  }, [isExpanded, onExpandClick, onCollapseClick]);

  // We wrap the header with a spacer div before and afterwards, to display correctly in the grid defined in basicViewerBase.tsx.
  return (
    <>
      <div />
      <StyledWrapperDiv>
        <StyledGroupButton isExpanded={isExpanded} onClick={onClick}>
          {renderContent(props)}
        </StyledGroupButton>
      </StyledWrapperDiv>
      {renderTimestampColumn(props)}
    </>
  );
};

function renderContent(props: BasicGroupHeaderProps) {
  if (props.isExpanded) {
    return (
      <StyledSeeLessDiv>
        <StyledTextDiv>
          <Trans id="activity-basic-group-collapse">See less</Trans>
        </StyledTextDiv>
      </StyledSeeLessDiv>
    );
  }

  const rankedActivities = computeRankedActivities(props);
  const iconName =
    rankedActivities.length === 1
      ? findIconForRankedActivity(rankedActivities[0]) || 'groupActivity'
      : 'groupActivity';

  return (
    <>
      <StyledIconDiv name={iconName} />
      <StyledTextDiv>{renderHeaderText(rankedActivities)}</StyledTextDiv>
    </>
  );
}

function renderTimestampColumn(props: BasicGroupHeaderProps) {
  if (props.isExpanded || !props.collapsedHeaderActivities.length) {
    return <div />;
  }

  const firstActivityDate = props.collapsedHeaderActivities[0].date;

  return (
    <StyledTimestampWrapperDiv>
      <StyledTimestampDiv>
        <InteractiveDateTimeFormatterContainer value={firstActivityDate} />
      </StyledTimestampDiv>
    </StyledTimestampWrapperDiv>
  );
}

function renderHeaderText(rankedActivities: VisualBasicCollapsedActivity[]) {
  // Find the first three activities with unique types.
  const uniqueActivities = _(rankedActivities)
    .uniqBy((activity) => activity.activityType)
    .slice(0, 3)
    .value();

  // The number of activities not represented in the unique array.
  const duplicateActivitiesCount = rankedActivities.length - uniqueActivities.length;

  // If we find 3+ unique activity (by type) and also have duplicate activities, we only display two unique activities.
  const headerActivities =
    uniqueActivities.length > 2 && duplicateActivitiesCount ? uniqueActivities.slice(0, 2) : uniqueActivities;

  const activitiesText = renderHeaderActivities(headerActivities);

  // The number of activities not being displayed in the header.
  const undisplayedActivitiesCount = computeExtraActivitiesCount(rankedActivities, headerActivities);

  // If we have additional activities not being displayed in the header, we display the count.
  if (undisplayedActivitiesCount) {
    return (
      <>
        {activitiesText} + {undisplayedActivitiesCount}{' '}
        <Trans id="activity-basic-group-expand-more">more</Trans>
      </>
    );
  }

  // Otherwise return just the names of the activities.
  return activitiesText;
}

function renderHeaderActivities(activities: ReadonlyArray<VisualBasicCollapsedActivity>) {
  return activities.map((activity, index) => {
    const isFirst = index === 0;
    const isLast = index + 1 === activities.length;

    return (
      <StyledActivitySpan key={activity.id}>
        {renderHeaderActivity(activity, isFirst)}
        {isLast ? '' : ', '}
      </StyledActivitySpan>
    );
  });
}

function computeRankedActivities(props: BasicGroupHeaderProps) {
  return _(props.collapsedHeaderActivities)
    .sortBy((activity) => activity.priority)
    .value();
}

// Determine the count to show for activities not displayed in the header.
// This accounts for activities which include a count of their own type when displayed.
function computeExtraActivitiesCount(
  rankedActivities: ReadonlyArray<VisualBasicCollapsedActivity>,
  headerActivities: ReadonlyArray<VisualBasicCollapsedActivity>,
) {
  // If we have a tag added activity or tag removed activity, we omit counting activities of the same type.
  // This is because the activity text for tagging/untagging includes its own count.
  const isTagAddedInHeader = headerActivities.some(
    (activity) => activity.activityType === BasicActivityTypesEnum.TAG,
  );
  const isTagRemovedInHeader = headerActivities.some(
    (activity) => activity.activityType === BasicActivityTypesEnum.UNTAG,
  );

  const extraActivities = rankedActivities.filter((rankedActivity) => {
    if (headerActivities.some((headerActivity) => headerActivity.id === rankedActivity.id)) {
      return false;
    }

    if (isTagAddedInHeader && rankedActivity.activityType === BasicActivityTypesEnum.TAG) {
      return false;
    }

    if (isTagRemovedInHeader && rankedActivity.activityType === BasicActivityTypesEnum.UNTAG) {
      return false;
    }

    return true;
  });

  return extraActivities.length;
}

function findIconForRankedActivity(activity: VisualBasicCollapsedActivity): IconName | undefined {
  switch (activity.type) {
    case VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_USER:
    case VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_TEAMMATE:
      return 'assignActivity';

    case VisualBasicCollapsedActivityTypesEnum.UNASSIGNED_FROM_USER:
    case VisualBasicCollapsedActivityTypesEnum.UNASSIGNED_FROM_TEAMMATE:
    case VisualBasicCollapsedActivityTypesEnum.UNASSIGNED_FROM_TEAMMATE_INACTIVITY:
    case VisualBasicCollapsedActivityTypesEnum.UNASSIGNED_FROM_TEAMMATE_DELETED:
      return 'unassignActivity';

    case VisualBasicCollapsedActivityTypesEnum.SNOOZE_EXPIRED:
    case VisualBasicCollapsedActivityTypesEnum.SNOOZED_IN_TEAM_INBOX:
      return 'snoozeActivity';

    case VisualBasicCollapsedActivityTypesEnum.USER_INVITED:
    case VisualBasicCollapsedActivityTypesEnum.TEAMMATE_INVITED:
      return 'inviteActivity';

    case VisualBasicCollapsedActivityTypesEnum.TEAMMATE_REMOVED:
      return 'removeActivity';

    case VisualBasicCollapsedActivityTypesEnum.MOVED_BY_TEAMMATE:
      return 'moveActivity';

    case VisualBasicCollapsedActivityTypesEnum.ARCHIVED_IN_TEAM_INBOX:
      return 'archiveActivity';

    case VisualBasicCollapsedActivityTypesEnum.REOPENED_IN_TEAM_INBOX:
    case VisualBasicCollapsedActivityTypesEnum.RESTORED_IN_TEAM_INBOX:
      return 'inboxActivity';

    case VisualBasicCollapsedActivityTypesEnum.TRASHED_IN_TEAM_INBOX:
      return 'trashActivity';

    case VisualBasicCollapsedActivityTypesEnum.TAG_ADDED_BY_TEAMMATE:
      return 'tagActivity';

    case VisualBasicCollapsedActivityTypesEnum.TAG_REMOVED_BY_TEAMMATE:
      return 'untagActivity';

    case VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_USER_BY_RULE:
    case VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_USER_BY_API:
    case VisualBasicCollapsedActivityTypesEnum.RULE_NOTIFIED_USER_WITH_RECIPIENT:
    case VisualBasicCollapsedActivityTypesEnum.RULE_NOTIFIED_USER_WITHOUT_RECIPIENT:
    case VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_TEAMMATE_BY_RULE:
    case VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_TEAMMATE_BY_API:
    case VisualBasicCollapsedActivityTypesEnum.MOVED_BY_RULE:
    case VisualBasicCollapsedActivityTypesEnum.TAG_ADDED_BY_RULE:
    case VisualBasicCollapsedActivityTypesEnum.TAG_REMOVED_BY_RULE:
    case VisualBasicCollapsedActivityTypesEnum.TAG_ADDED_BY_API:
    case VisualBasicCollapsedActivityTypesEnum.TAG_REMOVED_BY_API:
    case VisualBasicCollapsedActivityTypesEnum.RULE_NOTIFIED_TEAMMATE:
      return 'gearActivity';

    default:
      return undefined;
  }
}

function renderHeaderActivity(activity: VisualBasicCollapsedActivity, isFirst: boolean) {
  switch (activity.type) {
    case VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_USER:
    case VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_USER_BY_RULE:
    case VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_USER_BY_API:
      return (
        <Trans id="activity-basic-group-assign-you">
          <StyledFirstWordSpan $isFirst={isFirst}>assigned</StyledFirstWordSpan> to you
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.UNASSIGNED_FROM_USER:
    case VisualBasicCollapsedActivityTypesEnum.UNASSIGNED_FROM_TEAMMATE:
    case VisualBasicCollapsedActivityTypesEnum.UNASSIGNED_FROM_TEAMMATE_INACTIVITY:
    case VisualBasicCollapsedActivityTypesEnum.UNASSIGNED_FROM_TEAMMATE_DELETED:
      return (
        <Trans id="activity-basic-group-unassign">
          <StyledFirstWordSpan $isFirst={isFirst}>unassigned</StyledFirstWordSpan>
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.SNOOZE_EXPIRED:
      return (
        <Trans id="activity-basic-group-snooze-expire">
          <StyledFirstWordSpan $isFirst={isFirst}>reopened</StyledFirstWordSpan>
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.RULE_NOTIFIED_USER_WITH_RECIPIENT:
    case VisualBasicCollapsedActivityTypesEnum.RULE_NOTIFIED_USER_WITHOUT_RECIPIENT:
    case VisualBasicCollapsedActivityTypesEnum.USER_INVITED:
      return (
        <Trans id="activity-basic-group-invite-you">
          <StyledFirstWordSpan $isFirst={isFirst}>you</StyledFirstWordSpan> were invited
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_TEAMMATE:
    case VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_TEAMMATE_BY_RULE:
    case VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_TEAMMATE_BY_API:
      return (
        <Trans id="activity-basic-group-assign">
          <StyledFirstWordSpan $isFirst={isFirst}>assigned</StyledFirstWordSpan> to{' '}
          {buildTeammateDisplayName(activity.assignee)}
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.MOVED_BY_TEAMMATE:
    case VisualBasicCollapsedActivityTypesEnum.MOVED_BY_RULE:
      return (
        <Trans id="activity-basic-group-move">
          <StyledFirstWordSpan $isFirst={isFirst}>moved</StyledFirstWordSpan> to {activity.inbox.name}
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.ARCHIVED_IN_TEAM_INBOX:
      return (
        <Trans id="activity-basic-group-archive">
          <StyledFirstWordSpan $isFirst={isFirst}>archived</StyledFirstWordSpan>
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.SNOOZED_IN_TEAM_INBOX:
      return (
        <Trans id="activity-basic-group-snooze">
          <StyledFirstWordSpan $isFirst={isFirst}>snoozed</StyledFirstWordSpan>
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.REOPENED_IN_TEAM_INBOX:
      return (
        <Trans id="activity-basic-group-reoopen-teammate">
          <StyledFirstWordSpan $isFirst={isFirst}>reopened</StyledFirstWordSpan>
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.TRASHED_IN_TEAM_INBOX:
      return (
        <Trans id="activity-basic-group-trash">
          <StyledFirstWordSpan $isFirst={isFirst}>trashed</StyledFirstWordSpan>
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.RESTORED_IN_TEAM_INBOX:
      return (
        <Trans id="activity-basic-group-restore">
          <StyledFirstWordSpan $isFirst={isFirst}>restored</StyledFirstWordSpan>
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.TAG_ADDED_BY_TEAMMATE:
    case VisualBasicCollapsedActivityTypesEnum.TAG_ADDED_BY_RULE:
    case VisualBasicCollapsedActivityTypesEnum.TAG_ADDED_BY_API:
      return (
        <Plural
          id="mobile-activity-basic-group-tag-add"
          value={activity.addedTagCount}
          one={
            <>
              <StyledFirstWordSpan $isFirst={isFirst}>tag</StyledFirstWordSpan> added
            </>
          }
          other="# tags added"
        />
      );

    case VisualBasicCollapsedActivityTypesEnum.TAG_REMOVED_BY_TEAMMATE:
    case VisualBasicCollapsedActivityTypesEnum.TAG_REMOVED_BY_RULE:
    case VisualBasicCollapsedActivityTypesEnum.TAG_REMOVED_BY_API:
      return (
        <Plural
          id="mobile-activity-basic-group-tag-remove"
          value={activity.removedTagCount}
          one={
            <>
              <StyledFirstWordSpan $isFirst={isFirst}>tag</StyledFirstWordSpan> removed
            </>
          }
          other="# tags removed"
        />
      );

    case VisualBasicCollapsedActivityTypesEnum.TEAMMATE_INVITED:
      return (
        <Trans id="mobile-activity-basic-group-teammate-invite">
          {buildTeammateDisplayName(activity.recipient)} was invited
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.TEAMMATE_REMOVED:
      return (
        <Trans id="activity-basic-group-teammate-remove">
          {buildTeammateDisplayName(activity.recipient)} was removed
        </Trans>
      );

    case VisualBasicCollapsedActivityTypesEnum.RULE_NOTIFIED_TEAMMATE:
      return (
        <Trans id="mobile-activity-basic-group-notify-rule-teammate">
          {buildTeammateDisplayName(activity.recipient)} was notified
        </Trans>
      );

    default:
      return undefined;
  }
}
